import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps
} from '@mui/material'
import { FC, useCallback, useContext, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { RO_CLOSED } from '../../models/Status'
import { NotificationContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useUpdateRepairOrderMutation } from '../../redux/api'
import { RepairOrderExpanded } from '../../sdk'
import { ModifyObjectType } from '../../utils'
import { RepairOrderCloserDialog } from '../RepairOrder/RepairOrderCloser/RepairOrderCloserDialog'
import { ErrorMessageWrapper } from '../SharedUI'

type Props = ModifyObjectType<
  SelectProps,
  {
    repairOrder: RepairOrderExpanded
    disableClosure?: boolean
  }
>

export const ROStatusSelect: FC<Props> = ({
  repairOrder,
  disableClosure,
  ...props
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const { setError, setNotice } = useContext(NotificationContext)
  const [updateRepairOrder, { error, isLoading, isSuccess }] =
    useUpdateRepairOrderMutation()

  const [openRepairOrderCloserDialog, setOpenRepairOrderCloserDialog] =
    useState<boolean>(false)

  const handleChangeStatus = useCallback(
    async (event: SelectChangeEvent<unknown>) => {
      if (!repairOrder) {
        return
      }
      const { entry } = repairOrder
      const { localId, info, id } = entry!
      if (event.target.value === RO_CLOSED) {
        if (disableClosure) {
          setError(
            'Sorry, you may not close the repair order from this interface.'
          )
          event.preventDefault()
          return
        }
        setOpenRepairOrderCloserDialog(true)
      } else {
        await updateRepairOrder({
          authorization: accessToken,
          repairOrderEntry: {
            id,
            localId,
            info: {
              ...info,
              status:
                typeof event.target.value === 'string'
                  ? event.target.value
                  : undefined,
              closedOn: undefined
            }
          }
        })
      }
    },
    [accessToken, repairOrder, updateRepairOrder, disableClosure, setError]
  )

  const { repairOrderStatuses } = useContext(AppContext)

  useEffect(() => {
    if (isSuccess) {
      setNotice('Repair order status has been updated.')
    }
  }, [isSuccess, setNotice])

  return (
    <>
      <RepairOrderCloserDialog
        open={openRepairOrderCloserDialog}
        handleClose={() => setOpenRepairOrderCloserDialog(false)}
        repairOrderId={repairOrder.entry!.id!}
      />

      <ErrorMessageWrapper loading={isLoading} errorMessage={error}>
        <FormControl
          fullWidth
          size="small"
          data-testid="RepairOrderStatusSelect">
          <InputLabel>Status</InputLabel>
          <Select
            labelId="statusLabel"
            label="Status"
            id="status"
            data-testid="RepairOrderStatusInput"
            value={repairOrder?.status?.id || ''}
            onChange={handleChangeStatus}
            {...props}>
            {(repairOrderStatuses || []).map((status) => (
              <MenuItem
                key={status.id}
                value={status.id}
                data-testid={status.id}>
                {status.displayName!}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ErrorMessageWrapper>
    </>
  )
}
